import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  PictureAsPdf,
  Close,
  ExpandMore,
  CheckCircleOutlineOutlined,
  AccessTimeOutlined,
  DescriptionOutlined,
  PhotoLibraryOutlined,
  AssignmentOutlined,
  Compare,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { Project, ZenitalCamera } from '../types';
import PhotoButton from './PhotoButton';
import { getPhotoGroups, PhotoZone } from '../config/zones';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { captureException } from '../utils/captureException';

interface CaptureProps extends WithDeviceProps {
  project: Project;
  openCapture: boolean;
  handleClose: () => void;
  handleNextStep: (id: string) => void;
  refetch: () => void;
  onOpenPdf: () => void;
  setPdfType: (pdfType: string) => void;
  isSmallScreen: boolean;
  setProject: (project: Project) => void;
  handleCloseAll: () => void;
  zenitalCamera?: ZenitalCamera;
}

// Add this component before the Identification component (around line 61)
const NoTransition = React.forwardRef<any, any>(
  function NoTransition(props, ref) {
    const {
      children,
      appear,
      in: inProp,
      timeout,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      ownerState,
      ...rest
    } = props;
    // Return children directly without wrapper to preserve Dialog's layout
    return <>{children}</>;
  }
);

const Capture: React.FC<CaptureProps> = ({
  project,
  openCapture,
  handleClose,
  handleNextStep,
  refetch,
  onOpenPdf,
  setPdfType,
  isSmallScreen,
  setProject,
  handleCloseAll,
  zenitalCamera,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const photoGroups = getPhotoGroups(t);
  const [globalDisabled, setGlobalDisabled] = useState(false);

  useEffect(() => {
    const checkPhotos = async (allCompleted: "completed" | "pending") => {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        setError(t('capture.noTokenFound'));
        return;
      }
      axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        status_fotos: allCompleted ,
        warning_fotos: allCompleted === "completed" ? [] : [{id: 'photos', message: 'Falta capturar fotos obligatorias', check: false}],
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }).then(() => {
        setProject({
          ...project,
          status_fotos: allCompleted,
          warning_fotos: allCompleted === "completed" ? [] : [{id: 'photos', message: 'Falta capturar fotos obligatorias', check: false}],
        });
      }).catch((error) => {
        captureException(error, { tags: { feature: 'capture' }, extra: { action: 'checkPhotos', project_code: project?.project_code } });
        setError(error.response.data.error || t('capture.updateFailed'));
      });
    };

    if (project && photoGroups) {
      // Want to iterate for each group and check if the completed photos are greater than the total photos. If one element
      // has completedPhotos lower than totalPhotos, exit loop and return false.
      let allCompleted = "completed";
      photoGroups.forEach((group) => {
        const totalPhotos = group.photos.filter((button: PhotoZone) => button.mandatory).length; // 
        const completedPhotos = group.photos.filter((button: PhotoZone) => {
          const zoneFotos = project.fotos?.filter(foto => foto.zone === button.id) || [];
          return zoneFotos.length > 0 && zoneFotos.every(foto => foto.status === 'completed');
        }).length;
        if (completedPhotos < totalPhotos) {
          allCompleted = "pending";
          return;
        }
      });
      if (allCompleted === "completed" && project.status_fotos !== "completed") {
        checkPhotos("completed");
      } else if (allCompleted === "pending" && project.status_fotos !== "pending") {
        checkPhotos("pending");
      }
    }
  }, [photoGroups, project]);

  console.log("project", project);
  return (
    <Dialog
      fullScreen
      open={openCapture && project !== null}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        pt: isSmallScreen ? "56px" : "64px",
        pb: "56px",
      }}
      onClose={handleClose}
      slots={{ transition: NoTransition }}
      key={"capture"}
    >
      <DialogTitle sx={{ p: 0, bgcolor: 'background.light' }}>
        <AppBar
          color="inherit"
          position="fixed"
          sx={{
            // zIndex: (theme) => theme.zIndex.drawer,
            pt: isSmallScreen ? "56px" : "64px",
          }}
        >
          <Toolbar sx={{ p: 1 }}>
            <IconButton onClick={handleClose} color="inherit" sx={{ mr: 1 }}><ArrowBack/></IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }} />
            {/* <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('capture.title')}</Typography> */}
            {project?.gcs_series_book_path && (
              <Tooltip title={t('projectList.seriesBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('series');
                }}
                sx={{ mr: 0.5 }}><Compare /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_homologation_book_path && (
              <Tooltip title={t('projectList.homologationBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('homologation');
                }}
                sx={{ mr: 0.5 }}><AssignmentOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_image_book_path && (
              <Tooltip title={t('projectList.imageBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('image_book');
                }}
                sx={{ mr: 0.5 }}><PhotoLibraryOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_report_path && (
              <Tooltip title={t('projectList.reportTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('report');
                }}
                sx={{ mr: 0.5 }}><DescriptionOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_pdf_path && (
              <Tooltip title={t('projectList.launchTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('lanzamiento');
                }}
                sx={{ mr: 0.5 }}><PictureAsPdf /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleCloseAll} color="inherit"><Close /></IconButton>
          </Toolbar>
        </AppBar>
        {project && (
          <Box sx={{ py: 3, pl: 2, pr: 2, minHeight: "calc(100vh - 112px - " + (isSmallScreen ? "56px" : "72px") + ")", mt: isSmallScreen ? "56px" : "64px" }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {photoGroups.map((group) => {
                const totalPhotos = group.photos.filter((button: PhotoZone) => button.mandatory).length;
                const completedPhotos = group.photos.filter((button: PhotoZone) => {
                  const zoneFotos = project.fotos?.filter(foto => foto.zone === button.id) || [];
                  return zoneFotos.length > 0 && zoneFotos.every(foto => foto.status === 'completed');
                }).length;

                return (
                  <Accordion key={group.id} sx={{ '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 }, '& .MuiAccordionDetails-root': { paddingTop: 0 }, '& .MuiAccordionSummary-root': { margin: 0 }, borderRadius: '8px !important' }}>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                      sx={{ 
                        '&.Mui-expanded': { margin: 0 }, 
                        '& .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: "0px !important" },
                        '&.Mui-expanded .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: "0px !important" }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: "12px", fontSize: 18 }}>{group.label}</Typography>
                        {(completedPhotos>=totalPhotos) && <CheckCircleOutlineOutlined sx={{ color: 'success.main', mb: "12px", mr: 1 }} />}
                        {completedPhotos<totalPhotos && <AccessTimeOutlined sx={{ color: 'warning.main', mb: "12px", mr: 1 }} />}
                      </Box>
                    </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {group.photos.map((button) => {
                        const zoneFotos = project.fotos?.filter(foto => foto.zone === button.id) || [];
                        const completedZoneFotos = zoneFotos.filter(foto => foto.status === 'completed');
                        const hasCompletedPhotos = completedZoneFotos.length > 0;

                        return (
                          <PhotoButton
                            key={button.type}
                            type={button.type}
                            status={hasCompletedPhotos ? 'completed' : 'pending'}
                            warning={zoneFotos.length > 0 && (button.id === "Otros" || zoneFotos.every(foto => foto.global_classifier?.label === button.id || foto.global_classifier?.label === "Other")) ? false : true}
                            label={button.label}
                            id={button.id}
                            setError={setError}
                            project={project}
                            refetch={refetch}
                            mandatory={button.mandatory}
                            multiple={button.multiple}
                            photos={completedZoneFotos}
                            isMobile={isSmallScreen}
                            setProject={setProject}
                            globalDisabled={globalDisabled}
                            setGlobalDisabled={setGlobalDisabled}
                            doQualityCheck={button.qualityCheck}
                            zenitalCamera={zenitalCamera}
                          />
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                  </Accordion>
                );
              })}
              <Button variant="contained" color="primary" size="large" onClick={() => handleNextStep('image-capture')}>{t('capture.vehicle.next')}</Button>
            </Box>
          </Box>
        )}
      </DialogTitle>
      {error && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 200, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {error}
          <IconButton onClick={() => setError(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
    </Dialog>
  );
};

export default withDevice(React.memo(Capture));
